/**
 * Facet lists: checked entries first, and a filter box once a list is long.
 *
 * Every checkbox facet list (the Directory filter sheets, and the
 * MultiSelectCheckbox lists on the Directory's add and edit forms) orders
 * its entries the same way and offers the same filter:
 *
 *   - Checked entries sit at the top of the list, or at the top of their
 *     group in a grouped list, in the list's own order among themselves;
 *     the unchecked follow in the list's own order.
 *   - The order is taken when the list opens and again on each change to
 *     its filter text, never on a click: a row the person ticks stays
 *     under the pointer, so the next click lands where they aimed. A
 *     selection that changes from outside the list (a form prefill, a
 *     different sheet) is taken at once.
 *   - While a filter is typed, the checked entries stay visible above the
 *     matches, whether or not they match.
 *   - A list of more than FACET_FILTER_MIN entries gets a filter box.
 */

import { useState } from 'react';

/** A facet list longer than this many entries shows a filter box. */
export const FACET_FILTER_MIN = 10;

export function needsFacetFilter(entryCount: number): boolean {
  return entryCount > FACET_FILTER_MIN;
}

/** Case-insensitive substring match on the label; an empty query matches all. */
export function facetMatches(label: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  return q === '' || label.toLowerCase().includes(q);
}

/**
 * The entries to show, in order: the pinned (checked) ones first in their
 * own order, then the rest in their own order; with a query, the pinned
 * ones all stay and the rest are only those that match.
 */
export function orderFacetEntries<T>(
  entries: readonly T[],
  valueOf: (entry: T) => string,
  labelOf: (entry: T) => string,
  pinned: ReadonlySet<string>,
  query: string,
): T[] {
  const top: T[] = [];
  const rest: T[] = [];
  for (const entry of entries) {
    if (pinned.has(valueOf(entry))) top.push(entry);
    else if (facetMatches(labelOf(entry), query)) rest.push(entry);
  }
  return [...top, ...rest];
}

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((v) => set.has(v));
}

interface PinState {
  key: string;
  seen: readonly string[];
  own: readonly string[] | null;
  pinned: ReadonlySet<string>;
}

/**
 * The checked set a facet list sorts by. `resetKey` is whatever re-takes
 * the order (the open sheet and its filter text); `markOwn(next)` is called
 * by the list's own toggle just before it reports `next`, so that change
 * does not move rows.
 */
export function usePinnedSelection(
  selected: readonly string[],
  resetKey: string,
): { pinned: ReadonlySet<string>; markOwn: (next: readonly string[]) => void } {
  const [state, setState] = useState<PinState>(() => ({
    key: resetKey,
    seen: [...selected],
    own: null,
    pinned: new Set(selected),
  }));

  if (state.key !== resetKey) {
    setState({ key: resetKey, seen: [...selected], own: null, pinned: new Set(selected) });
  } else if (!sameMembers(state.seen, selected)) {
    const fromThisList = state.own !== null && sameMembers(state.own, selected);
    setState({
      key: resetKey,
      seen: [...selected],
      own: null,
      pinned: fromThisList ? state.pinned : new Set(selected),
    });
  }

  return {
    pinned: state.pinned,
    markOwn: (next) => setState((s) => ({ ...s, own: [...next] })),
  };
}
